import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CONFIG } from "../config/environment";
import { logger } from "../lib/logger";
import { supabase } from "../lib/supabase";

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: 'enterprise' | 'individual';
  organizationId?: string;
  role?: string;
  permissions: string[];
}

export function useAuth() {
  // Add QueryClient availability check
  let queryClient;
  try {
    queryClient = useQueryClient();
  } catch (error) {
    console.error('[useAuth] QueryClient not available:', error);
    // Return a fallback state when QueryClient is not available
    return {
      user: undefined,
      isLoading: false,
      isAuthenticated: false,
      error: new Error('QueryClient not available'),
      refetch: () => Promise.resolve(undefined),
      logout: () => Promise.resolve()
    };
  }

  // Check for logout parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const isLoggingOut = urlParams.get('logout') === 'true';
  const logoutCleared = urlParams.get('cleared') === 'true';

  const { data: user, isLoading, error, refetch } = useQuery({
    queryKey: ["auth/user"],
    staleTime: 5 * 60 * 1000, // 5 minutes cache for auth - PERFORMANCE BOOST
    gcTime: 15 * 60 * 1000, // 15 minutes garbage collection
    refetchOnWindowFocus: false, // Disable aggressive refetching - PERFORMANCE BOOST
    queryFn: async () => {
      // If we're in the middle of logout, don't make auth requests
      if (isLoggingOut) {
        logger.debug('[useAuth] Skipping auth request due to logout in progress');
        return null;
      }

      try {
        logger.debug('[useAuth] Starting auth request...');

        // First, check for Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user) {
          logger.debug('[useAuth] Found Supabase session for user:', session.user.email);

          // Convert Supabase user to our User format
          return {
            id: session.user.id,
            email: session.user.email || '',
            firstName: session.user.user_metadata?.first_name || session.user.email?.split('@')[0] || 'User',
            lastName: session.user.user_metadata?.last_name || '',
            userType: 'individual' as const,
            permissions: []
          };
        }

        // Fall back to Passport.js session check for backwards compatibility
        logger.debug('[useAuth] No Supabase session, checking Passport session...');

        // Ensure API_CONFIG is available
        if (!API_CONFIG || typeof API_CONFIG.getApiUrl !== 'function') {
          throw new Error('API_CONFIG not properly initialized');
        }

        const apiUrl = API_CONFIG.getApiUrl("auth/user");
        logger.debug('[useAuth] Making request to:', { apiUrl });
        
        const response = await fetch(apiUrl, {
          credentials: 'include',
          // Remove cache: 'no-cache' to allow browser caching - PERFORMANCE BOOST
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        logger.debug('[useAuth] Response received:', {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText
        });
        
        // Handle logout-related responses
        if (response.status === 401 || response.headers.get('X-Logout-Complete')) {
          logger.debug('[useAuth] Received 401 or X-Logout-Complete header. User is not authenticated.');
          return null; // Not authenticated
        }
        
        if (!response.ok) {
          logger.error('[useAuth] Fetch failed with status: ' + response.status);
          throw new Error('HTTP ' + response.status);
        }
        
        const userData = await response.json();
        logger.debug('[useAuth] Successfully fetched user data:', { userId: userData?.user?.id });
        
        // Debug: Log specific user fields for Display Name issue
        if (userData.user) {
        }
        
        // Ensure userData has the expected structure
        if (!userData || typeof userData !== 'object') {
          logger.error('[useAuth] Invalid user data format', { hasUser: !!userData });
          return null;
        }
        
        // Handle authentication response format
        if (userData.hasOwnProperty('authenticated')) {
          if (userData.authenticated === false || userData.user === null) {
            logger.debug('[useAuth] User not authenticated according to response');
            return null;
          }
          
          // Validate and sanitize user data to prevent array errors
          const user = userData.user;
          if (user && typeof user === 'object') {
            // Ensure permissions is always an array
            if (!Array.isArray(user.permissions)) {
              logger.warn('[useAuth] Fixing non-array permissions');
              user.permissions = user.permissions ? [user.permissions] : [];
            }
            
            // Ensure all required fields are present
            if (!user.id || !user.email) {
              logger.error('[useAuth] Invalid user data - missing required fields');
              return null;
            }
            
            return user;
          }
          
          logger.error('[useAuth] Invalid user data format in response');
          return null;
        }
        
        // Fallback for legacy response format
        const legacyUser = userData.user || userData;
        
        // If userData.user was null, don't use the fallback object
        if (userData.user === null) {
          logger.debug('[useAuth] userData.user is null, returning null');
          return null;
        }
        
        if (legacyUser && typeof legacyUser === 'object' && legacyUser.id) {
          // Ensure permissions is always an array for legacy format too
          if (!Array.isArray(legacyUser.permissions)) {
            logger.warn('[useAuth] Fixing non-array permissions in legacy format');
            legacyUser.permissions = legacyUser.permissions ? [legacyUser.permissions] : [];
          }
          return legacyUser;
        }
        
        logger.debug('[useAuth] No valid user found, returning null');
        return null;
      } catch (error) {
        logger.error('[useAuth] Error fetching user data', error);
        throw error;
      }
    },
    retry: (failureCount, error) => {
      return failureCount < 3;
    }
  });

  // Force logout state if URL indicates logout
  const finalUser = (isLoggingOut || logoutCleared) ? null : user;
  const finalIsAuthenticated = (isLoggingOut || logoutCleared) ? false : !!user;
  const finalIsLoading = (isLoggingOut || logoutCleared) ? false : isLoading;

  logger.debug('useAuth hook state:', { 
    hasUser: !!finalUser,
    isLoading: finalIsLoading, 
    hasError: !!error,
    isAuthenticated: finalIsAuthenticated
  });

  return {
    user: finalUser as User | undefined,
    isLoading: finalIsLoading,
    isAuthenticated: finalIsAuthenticated,
    error,
    refetch,
    logout: async () => {
      try {
        // First, sign out from Supabase
        await supabase.auth.signOut();

        // Clear local storage
        localStorage.removeItem('supabase_session');
        localStorage.removeItem('user');

        // Invalidate the query cache
        queryClient.invalidateQueries({ queryKey: ["auth/user"] });

        // Then try Passport logout for backwards compatibility
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        try {
          await fetch(API_CONFIG.getApiUrl('simple-logout'), {
            method: 'GET',
            credentials: 'include',
            cache: 'no-cache',
            signal: controller.signal,
            headers: {
              'Accept': 'application/json'
            }
          });
          clearTimeout(timeoutId);
        } catch {
          // Ignore Passport logout errors
        }

        // Redirect to login
        window.location.href = '/login?logout=success';
      } catch (error) {
        logger.error('[useAuth] Logout error:', error);
        // Still redirect even if logout fails
        window.location.href = '/login?logout=error';
      }
    }
  };
}